import { Instance } from 'mobx-state-tree';
import * as React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { TLCode } from '../../common/enums';
import { ColDef } from 'ag-grid-community';
import { inject, observer } from 'mobx-react';
import BaseGrid from '../common/grid/BaseGrid';
import styled from '@emotion/styled';
import { Button, Intent } from '@blueprintjs/core';
import { Message } from '../../store/models/Message';
import { Pharmacy } from '../../store/models/Pharmacy';
import { Group } from '../../store/models/Group';
import { dateFilterNormal, renderGridDateTimeWithDetails } from '../../store/utils';
import { MessageStore } from '../../store/MessageStore';
import { PharmacyStore } from '../../store';
import { AuthenticationStore } from '../../store';
import RTEInput from '../common/input/basicInputs/RTEInput';
import { MessageDialog } from '../common/MessageDialog';

interface MessageTabProps extends WithTranslateProps {
  initialMessageId?: number;
  recipientId?: number;
  secretariatId?: number;
  pharmacyId?: number;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  groupList: Array<Instance<typeof Group>>;
  refresh: () => void;
  MessageStore?: Instance<typeof MessageStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
}

interface MessageTabState {
  newMessage?: any;
  isTestMessage: boolean;
  isMessageDialogOpen: boolean;
  preview?: any;
  currentMessage?: any;
  messageList: Array<Instance<typeof Message>>;
  isLoading: boolean;
  dutyDate?: Date;
  dutyType?: string;
  isDayOnlyChecked?: boolean;
}

@inject('MessageStore', 'AuthenticationStore', 'PharmacyStore')
@observer
class MessageTab extends React.Component<MessageTabProps, MessageTabState> {
  state = {
    isLoading: false,
    newMessage: undefined,
    preview: '',
    messageList: [],
    isTestMessage: false,
    isMessageDialogOpen: false,
  } as MessageTabState;

  _isMounted = false;

  createMarkup = (text) => {
    return { __html: text };
  };

  tooltipRenderer = (text) => {
    const content = '<span dangerouslySetInnerHTML={__html: ' + text.value + '}></span>';
    return content;
  };

  columns = [
    {
      headerName: this.props.translate(TLCode.MESSAGE_Date),
      field: 'createdOn',
      width: 150,
      sort: 'desc',
      cellRenderer: renderGridDateTimeWithDetails,
      filterParams: {
        textCustomComparator: dateFilterNormal,
      },
    },
    { headerName: this.props.translate(TLCode.MESSAGE_Sender), field: 'senderName', width: 125 },
    { headerName: this.props.translate(TLCode.MESSAGE_Recipient), field: 'type', width: 125 },
    { headerName: this.props.translate(TLCode.MESSAGE_Title), field: 'title', width: 300 },
  ] as ColDef[];

  componentDidMount = async () => {
    this._isMounted = true;
    await this.loadMessage();
    await this.props.PharmacyStore!.checkListLoaded(); //loadList();
  };

  componentWillUnmount() {
    this._isMounted = false;
  }

  buildGridData = () => {
    return this.state.messageList.map((msg) => ({
      messageId: msg.messageId,
      createdOn: msg.createdOn,
      senderName: msg.senderName,
      recipientName: msg.recipientName,
      title: msg.title,
      body: msg.body,
      type: msg.type,
    }));
  };

  newMessage = () => {
    this.setState({ isTestMessage: false, isMessageDialogOpen: true });
  };

  testMessage = () => {
    this.setState({ isTestMessage: true, isMessageDialogOpen: true });
  };

  stopEdition = () => {
    this._isMounted && this.setState({ isMessageDialogOpen: false });
    console.log('SET STATE UNDEFINED');
  };

  private onRowClicked = (ev) => {
    this.setState({ preview: ev.data.body, currentMessage: ev.data });
  };

  loadMessage = async () => {
    this._isMounted && this.setState({ isLoading: true });
    const criteriaMessage: any = { secretariatId: this.props.secretariatId, pharmacyId: this.props.pharmacyId };
    const res: any = await this.props.MessageStore!.search(criteriaMessage);
    this._isMounted && this.setState({ messageList: res });
    this._isMounted && this.setState({ isLoading: false });
  };

  save = async (newMessage: any) => {
    this.stopEdition();
    await this.saveMessage(newMessage);
  };

  saveMessage = async (data: any) => {
    await this.props.MessageStore!.sendMessage(data);
    await this.loadMessage();
  };

  render() {
    const { translate, pharmacyId, pharmacyList, groupList } = this.props;
    const isSecretariat = this.props.AuthenticationStore!.isSecretariat;

    return (
      <Container>
        <ButtonsContainer>
          <Button
            text={translate(TLCode.GENERAL_Refresh)}
            intent={Intent.PRIMARY}
            onClick={() => (this.props.refresh ? this.props.refresh() : this.loadMessage())}
          />
          <Button
            text={translate(TLCode.MESSAGE_NewMessage)}
            intent={Intent.PRIMARY}
            onClick={() => this.newMessage()}
          />
          {this.props.AuthenticationStore!.isSuperAdmin && (
            <Button
              text={translate(TLCode.MESSAGE_TestMessage)}
              intent={Intent.PRIMARY}
              onClick={() => this.testMessage()}
            />
          )}
        </ButtonsContainer>
        <Cols>
          <Col>
            <BaseGrid
              columnDefs={this.columns}
              rowData={this.buildGridData()}
              keyName={'messageId'}
              onRowClicked={this.onRowClicked}
              loading={this.state.isLoading}
            />
          </Col>
          <Col>
            <MessageContainer>
              <RTEInput readOnly={true} value={this.state.preview} />
            </MessageContainer>
          </Col>
        </Cols>
        <MessageDialog
          isOpen={this.state.isMessageDialogOpen}
          onClose={this.stopEdition}
          isTest={this.state.isTestMessage}
          save={this.save}
          pharmacyId={pharmacyId}
          secretariatId={this.props.secretariatId}
          pharmacyList={pharmacyList}
          groupList={groupList}
          translate={translate}
          isSecretariat={isSecretariat}
        />
      </Container>
    );
  }
}

export default withTranslate(MessageTab);

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Cols = styled.div`
  display: flex;
  width: 100%
  height: 100%;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
`;

const Col = styled.div`
  display: flex;
  width: 50%
  height: 100%;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  flex: 1;
  border: 1px solid black;
  margin: 20px 0 0 10px;
`;

const ButtonsContainer = styled.div({
  display: 'flex',
  alignContent: 'center',
  justifyContent: 'flex-end',
  marginBottom: 5,
  '& button': {
    marginLeft: 5,
  },
});
